import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ExternalLink, LogOut, Settings, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { MediaDisplay } from '../MediaDisplay';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Sortable Project Card Component
const SortableProjectCard = ({ project, isAdmin, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: project.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      <Card
        className="hover:shadow-lg transition-shadow cursor-pointer"
        onClick={onClick}
      >
        {isAdmin && (
          <div
            {...attributes}
            {...listeners}
            className="absolute top-2 right-2 z-10 p-2 bg-white rounded-md shadow-md cursor-grab active:cursor-grabbing hover:bg-slate-100"
            onClick={(e) => e.stopPropagation()}
          >
            <GripVertical className="h-5 w-5 text-slate-500" />
          </div>
        )}
        {project.images && project.images.length > 0 && (
          <div className="aspect-video overflow-hidden rounded-t-lg bg-slate-100">
            <MediaDisplay
              url={project.images[0].url || project.images[0]}
              alt={project.images[0].description || project.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        <CardHeader>
          <CardTitle className="text-xl">
            <div dangerouslySetInnerHTML={{ __html: project.title }} />
          </CardTitle>
          <CardDescription className="line-clamp-3">
            <div dangerouslySetInnerHTML={{ __html: project.description }} />
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" size="sm" className="w-full">
            Ver Detalles
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export const ZonaProDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/zona-pro/projects`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setProjects(response.data);
    } catch (error) {
      toast.error('Error cargando proyectos');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = projects.findIndex((p) => p.id === active.id);
      const newIndex = projects.findIndex((p) => p.id === over.id);

      const newProjects = arrayMove(projects, oldIndex, newIndex);
      setProjects(newProjects);

      // Save new order to backend
      try {
        await axios.put(
          `${API_URL}/api/zona-pro/projects/reorder`,
          {
            items: newProjects.map((p) => p.id),
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success('Orden actualizado');
      } catch (error) {
        toast.error('Error actualizando el orden');
        // Revert on error
        loadProjects();
      }
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/zona-pro/login');
  };

  const isAdmin = user?.role === 'admin';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-serif font-bold text-slate-800">ZONA PRO</h1>
          <div className="flex gap-4 items-center">
            {user?.role === 'admin' && (
              <Button
                onClick={() => navigate('/zona-pro/admin')}
                variant="outline"
                size="sm"
              >
                <Settings className="mr-2 h-4 w-4" />
                Admin Panel
              </Button>
            )}
            <Button
              onClick={() => navigate('/zona-pro/cuestionario')}
              variant="outline"
              size="sm"
            >
              Cuestionario
            </Button>
            <Button onClick={handleLogout} variant="outline" size="sm">
              <LogOut className="mr-2 h-4 w-4" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="mb-8">
          <h2 className="text-3xl font-serif font-bold text-slate-800 mb-2">
            Casos y Cosas que te pueden interesar
          </h2>
          <p className="text-slate-600">
            Explora los proyectos profesionales de golf
            {isAdmin && <span className="ml-2 text-emerald-600 font-medium">(Arrastra para reordenar)</span>}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-500">Cargando proyectos...</p>
          </div>
        ) : projects.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-slate-500">No hay proyectos disponibles aún.</p>
            </CardContent>
          </Card>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={projects.map((p) => p.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {projects.map((project) => (
                  <SortableProjectCard
                    key={project.id}
                    project={project}
                    isAdmin={isAdmin}
                    onClick={() => navigate(`/zona-pro/proyecto/${project.id}`)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
};